import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Separator,
  Skeleton,
} from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import {
  ArrowLeft,
  CalendarDays,
  Clock,
  HelpCircle,
  Sun,
  UserCheck,
} from "lucide-react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { formatDateLocal, formatDateTimeLocal } from "@/shared/lib/date-time";
import { useOvertimeRequestQuery } from "../api/queries";
import { HrTour } from "../tour/HrTour";
import { overtimeRequestDetailTourSteps } from "../tour/hrTourSteps";
import { useHrTour } from "../tour/useHrTour";

const STATUS_LABELS: Record<string, string> = {
  pending: "ລໍຖ້າການອະນຸມັດ",
  approved: "ອະນຸມັດແລ້ວ",
  rejected: "ປະຕິເສດ",
};

const DAY_TYPE_LABELS: Record<"weekday" | "holiday", string> = {
  weekday: "ວັນທຳມະດາ",
  holiday: "ວັນພັກ",
};

function statusBadgeVariant(
  status: string,
): "default" | "destructive" | "secondary" {
  if (status === "approved") return "default";
  if (status === "rejected") return "destructive";
  return "secondary";
}

export function OvertimeRequestDetailPage() {
  const { id } = useParams({ from: "/app/hr/overtime/requests/$id" });
  const navigate = useNavigate();
  const { data, isLoading } = useOvertimeRequestQuery(id ?? "");
  const tour = useHrTour("hr-ot-detail-tour-completed");

  if (isLoading) {
    return (
      <>
        <Header />
        <Main>
          <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="space-y-2">
              <Skeleton className="h-8 w-64 max-w-full" />
              <Skeleton className="h-4 w-48" />
            </div>
            <Skeleton className="h-9 w-28" />
          </div>
          <div className="mx-auto w-full max-w-2xl space-y-4">
            <Skeleton className="h-36 w-full rounded-xl" />
            <Skeleton className="h-40 w-full rounded-xl" />
          </div>
        </Main>
      </>
    );
  }

  if (!data) {
    return (
      <>
        <Header />
        <Main>
          <p className="text-muted-foreground">ບໍ່ພົບຄຳຂໍ OT ນີ້.</p>
        </Main>
      </>
    );
  }

  const workDateLabel = formatDateLocal(data.workDate);
  const timeRangeLabel = `${data.startTime} – ${data.endTime}`;

  return (
    <>
      <Header />
      <Main>
        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="font-bold text-2xl tracking-tight sm:text-3xl">
                ລາຍລະອຽດຄຳຂໍ OT
              </h1>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-8 shrink-0"
                onClick={tour.startTour}
                aria-label="ເລີ່ມທົດສອບການນຳທາງ"
                title="ເລີ່ມທົດສອບການນຳທາງ"
              >
                <HelpCircle className="size-4" />
              </Button>
            </div>
            <p className="mt-2 max-w-xl text-muted-foreground">
              <span className="font-medium text-foreground">
                {workDateLabel}
              </span>
              <span className="mx-1.5 text-border">·</span>
              <span>
                {timeRangeLabel} ({data.hours} ຊົ່ວໂມງ)
              </span>
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() => navigate({ to: "/app/hr/overtime/requests" })}
            >
              <ArrowLeft className="size-4" />
              ກັບຄືນ
            </Button>
          </div>
        </div>

        <div className="mx-auto w-full max-w-2xl space-y-6">
          <div data-tourid="hr-ot-detail-card">
            <Card className="gap-0 overflow-hidden border-border/80 p-0 shadow-sm">
              <CardHeader className="space-y-1 border-border/60 border-b bg-muted/30 py-4">
                <CardTitle className="font-semibold text-lg tracking-tight">
                  ສະຫຼຸບຄຳຂໍ OT
                </CardTitle>
                <CardDescription>
                  ກວດເບິ່ງວັນທີ, ຊ່ວງເວລາ, ປະເພດວັນ, ແລະສະຖານະການອະນຸມັດ
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6 p-0">
                {data.status === "rejected" && data.rejectionReason ? (
                  <div className="border-border/60 border-b bg-destructive/5 px-6 py-4">
                    <Alert
                      variant="destructive"
                      className="border-destructive/40 bg-transparent"
                    >
                      <AlertTitle>ຄຳຂໍຖືກປະຕິເສດ</AlertTitle>
                      <AlertDescription className="mt-1 text-pretty">
                        {data.rejectionReason}
                      </AlertDescription>
                    </Alert>
                  </div>
                ) : null}

                <div className="px-6 pt-6">
                  <div className="flex flex-col gap-4 rounded-xl border border-primary/15 bg-linear-to-br from-primary/5 via-muted/40 to-muted/20 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
                    <div className="flex items-start gap-3">
                      <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                        <CalendarDays className="size-5" aria-hidden />
                      </div>
                      <div>
                        <p className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
                          ວັນທີເຮັດວຽກ OT
                        </p>
                        <p className="mt-0.5 font-semibold text-base text-foreground tracking-tight sm:text-lg">
                          {workDateLabel}
                        </p>
                        <p className="mt-1 font-medium text-muted-foreground text-sm">
                          {timeRangeLabel}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                      <span className="inline-flex items-center rounded-full border border-border/80 bg-background/80 px-3 py-1 font-medium text-sm tabular-nums shadow-sm">
                        {data.hours} ຊົ່ວໂມງ
                      </span>
                      <Badge
                        variant={statusBadgeVariant(data.status)}
                        className="px-2.5 py-0.5"
                      >
                        {STATUS_LABELS[data.status] ?? data.status}
                      </Badge>
                    </div>
                  </div>
                </div>

                <Separator className="bg-border/60" />

                <dl className="grid gap-3 px-6 sm:grid-cols-2 sm:gap-4">
                  <div className="flex gap-3 rounded-lg border border-border/50 bg-muted/20 p-4">
                    <div className="flex size-9 shrink-0 items-center justify-center rounded-md bg-background text-muted-foreground shadow-sm">
                      <CalendarDays className="size-4" aria-hidden />
                    </div>
                    <div className="min-w-0">
                      <dt className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
                        ວັນທີ
                      </dt>
                      <dd className="mt-1 font-medium leading-snug">
                        {workDateLabel}
                      </dd>
                    </div>
                  </div>
                  <div className="flex gap-3 rounded-lg border border-border/50 bg-muted/20 p-4">
                    <div className="flex size-9 shrink-0 items-center justify-center rounded-md bg-background text-muted-foreground shadow-sm">
                      <Clock className="size-4" aria-hidden />
                    </div>
                    <div className="min-w-0">
                      <dt className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
                        ເວລາ
                      </dt>
                      <dd className="mt-1 font-medium leading-snug">
                        {timeRangeLabel}{" "}
                        <span className="text-muted-foreground">
                          ({data.hours} ຊົ່ວໂມງ)
                        </span>
                      </dd>
                    </div>
                  </div>
                  <div className="flex gap-3 rounded-lg border border-border/50 bg-muted/20 p-4 sm:col-span-2">
                    <div className="flex size-9 shrink-0 items-center justify-center rounded-md bg-background text-muted-foreground shadow-sm">
                      <Sun className="size-4" aria-hidden />
                    </div>
                    <div className="min-w-0">
                      <dt className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
                        ປະເພດວັນ
                      </dt>
                      <dd className="mt-1 font-medium leading-snug">
                        {DAY_TYPE_LABELS[data.dayType] ?? data.dayType}
                      </dd>
                    </div>
                  </div>
                  <div className="flex gap-3 rounded-lg border border-border/50 bg-muted/20 p-4 sm:col-span-2">
                    <div className="flex size-9 shrink-0 items-center justify-center rounded-md bg-background text-muted-foreground shadow-sm">
                      <UserCheck className="size-4" aria-hidden />
                    </div>
                    <div className="min-w-0 flex-1">
                      <dt className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
                        ການອະນຸມັດ
                      </dt>
                      <dd className="mt-1 space-y-1 text-sm leading-relaxed">
                        {data.status === "approved" && data.approvedAt ? (
                          <>
                            <p className="font-medium text-foreground">
                              ອະນຸມັດເມື່ອ {formatDateTimeLocal(data.approvedAt)}
                            </p>
                            {data.approvedByName ?? data.approvedBy ? (
                              <p className="text-muted-foreground">
                                ໂດຍ{" "}
                                <span className="text-foreground">
                                  {data.approvedByName ?? data.approvedBy}
                                </span>
                              </p>
                            ) : null}
                          </>
                        ) : data.status === "pending" ? (
                          <span className="font-medium text-muted-foreground">
                            ລໍຖ້າການພິຈາລະນາຈາກຜູ້ອະນຸມັດ
                          </span>
                        ) : (
                          <span className="font-medium text-muted-foreground">
                            —
                          </span>
                        )}
                      </dd>
                    </div>
                  </div>
                </dl>

                {data.reason ? (
                  <>
                    <Separator className="bg-border/60" />
                    <div className="px-6 pb-2">
                      <div className="rounded-xl border border-border/60 bg-muted/25 p-4 sm:p-5">
                        <p className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
                          ເຫດຜົນ
                        </p>
                        <p className="mt-2 whitespace-pre-wrap text-pretty text-foreground text-sm leading-relaxed">
                          {data.reason}
                        </p>
                      </div>
                    </div>
                  </>
                ) : null}

                <div className="border-border/60 border-t bg-muted/20 px-6 py-3">
                  <p className="text-center text-muted-foreground text-xs">
                    ສ້າງເມື່ອ {formatDateTimeLocal(data.createdAt)}
                    {data.updatedAt !== data.createdAt ? (
                      <> · ອັບເດດລ່າສຸດ {formatDateTimeLocal(data.updatedAt)}</>
                    ) : null}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="flex justify-center pb-2">
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground"
              onClick={() => navigate({ to: "/app/hr/overtime/requests" })}
            >
              <ArrowLeft className="mr-1 size-4" />
              ກັບໄປລາຍການຄຳຂໍ OT
            </Button>
          </div>

          <HrTour
            steps={overtimeRequestDetailTourSteps}
            run={tour.run}
            onCallback={tour.handleJoyrideCallback}
          />
        </div>
      </Main>
    </>
  );
}
